import React, { useState, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@mui/styles";
import { green } from "@mui/material/colors";
import {
	Button,
	TableBody,
	TableRow,
	TableCell,
	IconButton,
	Table,
	TableHead,
	Paper,
	Tooltip,
	Typography,
	CircularProgress,
} from "@mui/material";
import {
	Edit,
	CheckCircle,
	SignalCellularConnectedNoInternet2Bar,
	SignalCellularConnectedNoInternet0Bar,
	SignalCellular4Bar,
	CropFree,
	DeleteOutline,
	Block,
	Group as GroupIcon,
	AccountCircleOutlined as ProfileIcon,
	PhoneAndroid as PhoneAndroidIcon,
	Campaign as CampaignIcon,
	MonitorHeart as DiagnosticsIcon,
	Label as LabelIcon,
	WifiTethering as OnlineIcon,
	WifiTetheringOff as OfflineIcon,
	Add as AddLabelIcon,
	Delete as DeleteLabelIcon,
} from "@mui/icons-material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import WhatsAppProfileModal from "../../components/WhatsAppProfileModal";
import StatusModal from "../../components/StatusModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	customTableCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	tooltip: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		fontSize: theme.typography.pxToRem(14),
		border: "1px solid #dadde9",
		maxWidth: 450,
	},
	tooltipPopper: {
		textAlign: "center",
	},
	buttonProgress: {
		color: green[500],
	},
}));

const CustomToolTip = ({ title, content, children }) => {
	const classes = useStyles();

	return (
		<Tooltip
			arrow
			classes={{
				tooltip: classes.tooltip,
				popper: classes.tooltipPopper,
			}}
			title={
				<React.Fragment>
					<Typography gutterBottom color="inherit">
						{title}
					</Typography>
					{content && <Typography>{content}</Typography>}
				</React.Fragment>
			}
		>
			{children}
		</Tooltip>
	);
};

const Connections = () => {
	const classes = useStyles();

	const { whatsApps, loading, refresh } = useContext(WhatsAppsContext);
	const { user } = useContext(AuthContext);
	const isAdmin = user?.profile === "admin";
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [blockListOpen, setBlockListOpen] = useState(false);
	const [blockListWaId, setBlockListWaId] = useState(null);
	const [blockList, setBlockList] = useState([]);
	const [blockListLoading, setBlockListLoading] = useState(false);
	const [createGroupOpen, setCreateGroupOpen] = useState(false);
	const [createGroupWaId, setCreateGroupWaId] = useState(null);
	const [profileModalOpen, setProfileModalOpen] = useState(false);
	const [statusOpen, setStatusOpen] = useState(false);
	const [statusWaId, setStatusWaId] = useState(null);
	const [diagOpen, setDiagOpen] = useState(false);
	const [diagData, setDiagData] = useState(null);
	const [diagLoading, setDiagLoading] = useState(false);

	const handleDiagnostics = async (whatsappId) => {
		setDiagOpen(true);
		setDiagLoading(true);
		setDiagData(null);
		try {
			const { data } = await api.get(`/whatsapp/${whatsappId}/diagnostics`);
			setDiagData(data);
		} catch (err) {
			toastError(err);
			setDiagOpen(false);
		} finally {
			setDiagLoading(false);
		}
	};
	const [profileWaId, setProfileWaId] = useState(null);
	const [widOpen, setWidOpen] = useState(false);
	const [widValue, setWidValue] = useState("");
	const [widLoading, setWidLoading] = useState(false);
	const [groupName, setGroupName] = useState("");
	const [labelsWaId, setLabelsWaId] = useState(null);
	const [labelsOpen, setLabelsOpen] = useState(false);
	const [labelsList, setLabelsList] = useState([]);
	const [labelsLoading, setLabelsLoading] = useState(false);
	const [newLabelName, setNewLabelName] = useState("");
	const [newLabelColor, setNewLabelColor] = useState("#0078d4");
	const [presenceLoading, setPresenceLoading] = useState({});
	const [groupParticipants, setGroupParticipants] = useState("");
	const [groupCreating, setGroupCreating] = useState(false);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	const handleStartWhatsAppSession = async whatsAppId => {
		try {
			await api.post(`/whatsappsession/${whatsAppId}`);
			refresh();
		} catch (err) {
			toastError(err);
		}
	};

	const handleRequestNewQrCode = async whatsAppId => {
		try {
			await api.put(`/whatsappsession/${whatsAppId}`);
			refresh();
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleCreateGroup = async () => {
		const participants = groupParticipants.split(/[\n,;]/).map(s => s.replace(/\D/g, "")).filter(Boolean);
		if (!groupName.trim() || participants.length === 0) {
			toast.error("Nome e pelo menos 1 participante são obrigatórios.");
			return;
		}
		setGroupCreating(true);
		try {
			await api.post("/groups", { whatsappId: createGroupWaId, name: groupName.trim(), participants });
			toast.success("Grupo criado com sucesso!");
			setCreateGroupOpen(false);
			setGroupName("");
			setGroupParticipants("");
		} catch (err) {
			toastError(err);
		} finally {
			setGroupCreating(false);
		}
	};

	const handleShowWid = async (waId) => {
		setWidValue("");
		setWidOpen(true);
		setWidLoading(true);
		try {
			const { data } = await api.get(`/whatsapp/${waId}/connection-status`);
			setWidValue(data.wid || "—");
		} catch (err) {
			toastError(err);
			setWidOpen(false);
		} finally {
			setWidLoading(false);
		}
	};

	const handleOpenLabels = async (waId) => {
		setLabelsWaId(waId);
		setLabelsOpen(true);
		setLabelsLoading(true);
		try {
			const { data } = await api.get(`/whatsapp/${waId}/labels`);
			setLabelsList(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
			setLabelsList([]);
		} finally {
			setLabelsLoading(false);
		}
	};

	const handleCreateLabel = async () => {
		if (!newLabelName.trim() || !labelsWaId) return;
		try {
			const { data } = await api.post(`/whatsapp/${labelsWaId}/labels/new`, { name: newLabelName.trim(), color: newLabelColor });
			setLabelsList(prev => [...prev, data]);
			setNewLabelName("");
			toast.success(`Etiqueta "${data.name}" criada!`);
		} catch (err) { toastError(err); }
	};

	const handleDeleteLabel = async (labelId, labelName) => {
		if (!labelsWaId) return;
		try {
			await api.delete(`/whatsapp/${labelsWaId}/labels/${labelId}`);
			setLabelsList(prev => prev.filter(l => l.id !== labelId));
			toast.success(`Etiqueta "${labelName}" removida.`);
		} catch (err) { toastError(err); }
	};

	const handleTogglePresence = async (waId, online) => {
		setPresenceLoading(prev => ({ ...prev, [waId]: true }));
		try {
			await api.post(`/whatsapp/${waId}/presence`, { online });
			toast.success(online ? 'Presença ativa.' : 'Presença desativada.');
		} catch (err) { toastError(err); } finally {
			setPresenceLoading(prev => ({ ...prev, [waId]: false }));
		}
	};

	const handleOpenBlockList = async (waId) => {
		setBlockListWaId(waId);
		setBlockListOpen(true);
		setBlockListLoading(true);
		try {
			const { data } = await api.get(`/whatsapp/${waId}/blocklist`);
			setBlockList(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
			setBlockList([]);
		} finally {
			setBlockListLoading(false);
		}
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId: whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
				refresh();
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
				refresh();
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
	};

	const renderActionButtons = whatsApp => {
		return (
			<>
				{whatsApp.status === "qrcode" && (
					<Button
						size="small"
						variant="contained"
						color="primary"
						onClick={() => handleOpenQrModal(whatsApp)}
					>
						{i18n.t("connections.buttons.qrcode")}
					</Button>
				)}
				{whatsApp.status === "DISCONNECTED" && (
					<>
						<Button
							size="small"
							variant="outlined"
							color="primary"
							onClick={() => handleStartWhatsAppSession(whatsApp.id)}
						>
							{i18n.t("connections.buttons.tryAgain")}
						</Button>{" "}
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							onClick={() => handleRequestNewQrCode(whatsApp.id)}
						>
							{i18n.t("connections.buttons.newQr")}
						</Button>
					</>
				)}
				{(whatsApp.status === "CONNECTED" ||
					whatsApp.status === "PAIRING" ||
					whatsApp.status === "TIMEOUT") && (
					<Button
						size="small"
						variant="outlined"
						color="secondary"
						onClick={() => {
							handleOpenConfirmationModal("disconnect", whatsApp.id);
						}}
					>
						{i18n.t("connections.buttons.disconnect")}
					</Button>
				)}
				{whatsApp.status === "OPENING" && (
					<Button
						size="small"
						variant="outlined"
						color="secondary"
						onClick={() => handleRequestNewQrCode(whatsApp.id)}
					>
						{i18n.t("connections.buttons.connecting")} · {i18n.t("connections.buttons.newQr")}
					</Button>
				)}
				{whatsApp.status === "FAILED" && (
					<>
						<Button
							size="small"
							variant="outlined"
							color="primary"
							onClick={() => handleStartWhatsAppSession(whatsApp.id)}
						>
							{i18n.t("connections.buttons.tryAgain")}
						</Button>{" "}
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							onClick={() => handleRequestNewQrCode(whatsApp.id)}
						>
							{i18n.t("connections.buttons.newQr")}
						</Button>
					</>
				)}
			</>
		);
	};

	const STATUS_CONFIG = {
		CONNECTED:    { icon: <SignalCellular4Bar style={{ color: green[500], fontSize: 18 }} />,                        label: "Conectado",      color: green[600] },
		DISCONNECTED: { icon: <SignalCellularConnectedNoInternet0Bar color="secondary" style={{ fontSize: 18 }} />,      label: "Desconectado",   color: "#9e9e9e" },
		OPENING:      { icon: <CircularProgress size={16} className={classes.buttonProgress} />,                         label: "Conectando…",    color: "#fb8c00" },
		qrcode:       { icon: <CropFree style={{ fontSize: 18, color: "#9e9e9e" }} />,                                   label: "Aguardando QR",  color: "#9e9e9e" },
		TIMEOUT:      { icon: <SignalCellularConnectedNoInternet2Bar color="secondary" style={{ fontSize: 18 }} />,       label: "Timeout",        color: "#ff7043" },
		PAIRING:      { icon: <SignalCellularConnectedNoInternet2Bar color="secondary" style={{ fontSize: 18 }} />,       label: "Pareando…",      color: "#fb8c00" },
		FAILED:       { icon: <SignalCellularConnectedNoInternet0Bar color="error" style={{ fontSize: 18 }} />,           label: "Falha",          color: "#d32f2f" },
	};

	const renderStatusToolTips = whatsApp => {
		const cfg = STATUS_CONFIG[whatsApp.status] || { icon: null, label: whatsApp.status || "—", color: "#9e9e9e" };
		return (
			<div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
				{cfg.icon}
				<span style={{ fontSize: "0.78rem", fontWeight: 600, color: cfg.color, whiteSpace: "nowrap" }}>
					{cfg.label}
				</span>
			</div>
		);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
			/>
			<Dialog open={createGroupOpen} onClose={() => setCreateGroupOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Criar grupo WhatsApp</DialogTitle>
				<DialogContent>
					<TextField
						label="Nome do grupo"
						fullWidth
						variant="outlined"
						margin="dense"
						value={groupName}
						onChange={e => setGroupName(e.target.value)}
					/>
					<TextField
						label="Participantes (números separados por vírgula ou linha)"
						fullWidth
						variant="outlined"
						margin="dense"
						multiline
						rows={3}
						placeholder="5511999999999, 5511888888888"
						value={groupParticipants}
						onChange={e => setGroupParticipants(e.target.value)}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCreateGroupOpen(false)}>Cancelar</Button>
					<Button variant="contained" color="primary" onClick={handleCreateGroup} disabled={groupCreating}>
						{groupCreating ? "Criando..." : "Criar grupo"}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={blockListOpen} onClose={() => setBlockListOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Contatos bloqueados</DialogTitle>
				<DialogContent>
					{blockListLoading ? (
						<CircularProgress size={24} />
					) : blockList.length === 0 ? (
						<Typography>Nenhum contato bloqueado.</Typography>
					) : (
						<List dense>
							{blockList.map((num, i) => (
								<ListItem key={i}>
									<ListItemText primary={typeof num === "string" ? num.replace("@c.us", "") : JSON.stringify(num)} />
								</ListItem>
							))}
						</List>
					)}
				</DialogContent>
			</Dialog>
			<MainHeader>
				<Title>{i18n.t("connections.title")}</Title>
				{isAdmin && (
					<MainHeaderButtonsWrapper>
						<Button
							variant="contained"
							color="primary"
							onClick={handleOpenWhatsAppModal}
						>
							{i18n.t("connections.buttons.add")}
						</Button>
					</MainHeaderButtonsWrapper>
				)}
			</MainHeader>
			<Paper className={classes.mainPaper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="center">
								{i18n.t("connections.table.name")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.status")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.session")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.lastUpdate")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.default")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.actions")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRowSkeleton />
						) : (
							<>
								{whatsApps?.length > 0 &&
									whatsApps.map(whatsApp => (
										<TableRow key={whatsApp.id}>
											<TableCell align="center">{whatsApp.name}</TableCell>
											<TableCell align="center">
												{renderStatusToolTips(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{isAdmin && renderActionButtons(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{whatsApp.updatedAt ? format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm") : ""}
											</TableCell>
											<TableCell align="center">
												{whatsApp.isDefault && (
													<div className={classes.customTableCell}>
														<CheckCircle style={{ color: green[500] }} />
													</div>
												)}
											</TableCell>
											<TableCell align="center">
												{isAdmin && (
													<>
														<IconButton
															size="small"
															onClick={() => handleEditWhatsApp(whatsApp)}
														>
															<Edit />
														</IconButton>
														{whatsApp.status === "CONNECTED" && (
															<>
																<Tooltip title="Ver número conectado">
																	<IconButton size="small" onClick={() => handleShowWid(whatsApp.id)}>
																		<PhoneAndroidIcon />
																	</IconButton>
																</Tooltip>
																<Tooltip title="Editar perfil do WhatsApp">
																<IconButton size="small" onClick={() => { setProfileWaId(whatsApp.id); setProfileModalOpen(true); }}>
																	<ProfileIcon />
																</IconButton>
															</Tooltip>
																<Tooltip title="Criar grupo">
																	<IconButton
																		size="small"
																		onClick={() => { setCreateGroupWaId(whatsApp.id); setCreateGroupOpen(true); }}
																	>
																		<GroupIcon />
																	</IconButton>
																</Tooltip>
																<Tooltip title="Publicar Status (campanha)">
																	<IconButton
																		size="small"
																		onClick={() => { setStatusWaId(whatsApp.id); setStatusOpen(true); }}
																	>
																		<CampaignIcon />
																	</IconButton>
																</Tooltip>
																<Tooltip title="Diagnóstico (bateria/dispositivo)">
																	<IconButton size="small" onClick={() => handleDiagnostics(whatsApp.id)}>
																		<DiagnosticsIcon />
																	</IconButton>
																</Tooltip>
																<Tooltip title="Lista de bloqueados">
																	<IconButton
																		size="small"
																		onClick={() => handleOpenBlockList(whatsApp.id)}
																	>
																		<Block />
																	</IconButton>
																</Tooltip>
																<Tooltip title="Gerenciar etiquetas WA">
																	<IconButton size="small" onClick={() => handleOpenLabels(whatsApp.id)}>
																		<LabelIcon />
																	</IconButton>
																</Tooltip>
																<Tooltip title={presenceLoading[whatsApp.id] ? "Aguarde..." : "Ativar presença online"}>
																	<IconButton size="small" disabled={!!presenceLoading[whatsApp.id]} onClick={() => handleTogglePresence(whatsApp.id, true)}>
																		<OnlineIcon style={{ color: "#4caf50" }} />
																	</IconButton>
																</Tooltip>
															</>
														)}
														<IconButton
															size="small"
															onClick={e => {
																handleOpenConfirmationModal("delete", whatsApp.id);
															}}
														>
															<DeleteOutline />
														</IconButton>
													</>
												)}
											</TableCell>
										</TableRow>
									))}
							</>
						)}
					</TableBody>
				</Table>
			</Paper>
			<Dialog open={widOpen} onClose={() => setWidOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Número conectado</DialogTitle>
				<DialogContent>
					{widLoading ? (
						<CircularProgress size={24} />
					) : (
						<Typography variant="h6" style={{ textAlign: "center", padding: "8px 0" }}>
							{widValue}
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setWidOpen(false)}>Fechar</Button>
				</DialogActions>
			</Dialog>
			<WhatsAppProfileModal
				open={profileModalOpen}
				onClose={() => setProfileModalOpen(false)}
				whatsappId={profileWaId}
			/>
			<StatusModal
				open={statusOpen}
				onClose={() => setStatusOpen(false)}
				whatsappId={statusWaId}
			/>
			<Dialog open={diagOpen} onClose={() => setDiagOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Diagnóstico da conexão</DialogTitle>
				<DialogContent dividers>
					{diagLoading ? (
						<div style={{ textAlign: "center", padding: 16 }}><CircularProgress size={28} /></div>
					) : diagData ? (
						<div style={{ fontSize: 14, lineHeight: 1.8 }}>
							<div>🔋 Bateria: <strong>{diagData.battery != null ? `${diagData.battery}%` : "—"}</strong></div>
							<div>🔌 Logado: <strong>{diagData.loggedIn ? "Sim" : "Não"}</strong></div>
							<div>🟢 Online: <strong>{diagData.online ? "Sim" : "Não"}</strong></div>
							{diagData.device && (
								<div style={{ marginTop: 6 }}>📱 Dispositivo: <strong>{diagData.device?.phone?.device_manufacturer || diagData.device?.platform || "—"}</strong></div>
							)}
						</div>
					) : (
						<Typography variant="body2" style={{ color: "#888" }}>Sem dados.</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDiagOpen(false)}>Fechar</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={labelsOpen} onClose={() => setLabelsOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Etiquetas do WhatsApp</DialogTitle>
				<DialogContent>
					{labelsLoading ? (
						<CircularProgress size={24} />
					) : (
						<>
							<List dense>
								{labelsList.length === 0 && (
									<ListItem><ListItemText secondary="Nenhuma etiqueta encontrada." /></ListItem>
								)}
								{labelsList.map(label => (
									<ListItem key={label.id} secondaryAction={
										<IconButton edge="end" size="small" onClick={() => handleDeleteLabel(label.id, label.name)}>
											<DeleteLabelIcon fontSize="small" />
										</IconButton>
									}>
										<ListItemText primary={
											<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
												<span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: label.color, flexShrink: 0 }} />
												{label.name}
											</span>
										} />
									</ListItem>
								))}
							</List>
							<div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
								<input type="color" value={newLabelColor} onChange={e => setNewLabelColor(e.target.value)} style={{ width: 36, height: 36, border: 'none', cursor: 'pointer' }} />
								<TextField size="small" variant="outlined" placeholder="Nova etiqueta" value={newLabelName}
									onChange={e => setNewLabelName(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleCreateLabel()} style={{ flex: 1 }} />
								<Button variant="contained" color="primary" size="small" onClick={handleCreateLabel} startIcon={<AddLabelIcon />}>Criar</Button>
							</div>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setLabelsOpen(false)}>Fechar</Button>
				</DialogActions>
			</Dialog>
		</MainContainer>
	);
};

export default Connections;
